import { Input } from '@/components/ui';
import { PROJECT_STATUSES, type ProjectCreateFormData } from '../types';

interface ProjectSectionProps {
  formData: ProjectCreateFormData;
  onChange: (field: string, value: string) => void;
  errors: Record<string, string>;
  disabled?: boolean;
}

export function ProjectSection({
  formData,
  onChange,
  errors,
  disabled = false,
}: ProjectSectionProps) {
  return (
    <div className="bg-white rounded-xl border border-secondary-200 p-6">
      {/* Section Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-primary-100 text-primary-600 rounded-lg flex items-center justify-center">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-secondary-900">
            Project Details
          </h2>
          <p className="text-sm text-secondary-500">
            Enter the project information
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Project Name */}
        <Input
          label="Project Name"
          name="projectName"
          value={formData.projectName}
          onChange={(e) => onChange('projectName', e.target.value)}
          placeholder="Kitchen Renovation"
          error={errors.projectName}
          disabled={disabled}
          required
        />

        {/* Status */}
        <div className="w-full">
          <label htmlFor="projectStatus" className="input-label">
            Status
          </label>
          <select
            id="projectStatus"
            name="projectStatus"
            value={formData.projectStatus}
            onChange={(e) => onChange('projectStatus', e.target.value)}
            className={`input-field ${errors.projectStatus ? 'input-error' : ''}`}
            disabled={disabled}
          >
            {PROJECT_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
          {errors.projectStatus && (
            <p className="error-message">{errors.projectStatus}</p>
          )}
        </div>

        {/* Date Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Start Date (Optional)"
            name="projectStartDate"
            type="date"
            value={formData.projectStartDate}
            onChange={(e) => onChange('projectStartDate', e.target.value)}
            error={errors.projectStartDate}
            disabled={disabled}
          />
          <Input
            label="End Date (Optional)"
            name="projectEndDate"
            type="date"
            value={formData.projectEndDate}
            onChange={(e) => onChange('projectEndDate', e.target.value)}
            error={errors.projectEndDate}
            disabled={disabled}
          />
        </div>

        {/* Date Validation Warning */}
        {formData.projectStartDate &&
          formData.projectEndDate &&
          formData.projectEndDate < formData.projectStartDate && (
            <p className="text-sm text-amber-600 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              End date is before start date
            </p>
          )}
      </div>
    </div>
  );
}